/* istanbul ignore file */
import { useCallback, useContext } from 'react';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import { useWizardService, getFlowFactors, WIZARD_EVENTS } from '@bingads-webui-react/wizard';
import { PMAX_LITE_WIZARD_STEP_PATHS } from '@bingads-webui-campaign-react/pmax-lite-wizard-utils';
import {
  useUserCreationContext,
  usePmaxCampaignCRUD,
} from '@bingads-webui-campaign-react/pmax-lite-data';

import { assetGroupViewModelFromCache } from './model';
import { useCreateTempAssetGroup } from './use-create-temp-asset-group';
import { useAdAssetsInstrument } from './use-ad-assets-instrument';

export const useReadFromCache = ({
  createAssetGroupWhileLoading,
  cache,
}) => {
  const { state, send } = useWizardService();
  const { scope } = useContext(ScopeContext);
  const {
    campaignId, assetGroupId,
  } = scope.values;
  const serverContext = useContext(ServerContext);
  const { CurrentAccount } = serverContext;
  const flowFactors = getFlowFactors(state);
  const { isCollectBusinessInfo } = flowFactors || {};

  const uiActivity = useAdAssetsInstrument();
  const { createUser, createUserPromise, isCreatingUser } = useUserCreationContext();

  const { createOrUpdateCampaignPromise } = usePmaxCampaignCRUD();

  const createAssetGroupWithTempAssets = useCreateTempAssetGroup();

  const createAccountAndCampaign = useCallback(() => {
    let accountPromise = () => Promise.resolve(serverContext);
    let campaignPromise = () => Promise.resolve({ Id: campaignId });
    if (isCreatingUser) {
      accountPromise = () => createUserPromise;
    } else if (!CurrentAccount) {
      accountPromise = createUser;
    }
    if (!campaignId) {
      campaignPromise = () => createOrUpdateCampaignPromise();
    }

    return accountPromise().then(serverResponse => Promise.all([serverResponse, campaignPromise()]));
  }, [CurrentAccount, campaignId, createOrUpdateCampaignPromise,
    createUser, createUserPromise, isCreatingUser, serverContext]);

  const readFromCache = useCallback(() => createAccountAndCampaign().then(([serverResponse, campaignResponse]) => {
    const { Id: cmpId } = campaignResponse;
    const { website } = cache;

    const assetGroupData = assetGroupViewModelFromCache(cache);

    if (createAssetGroupWhileLoading && !assetGroupId) {
      return createAssetGroupWithTempAssets(assetGroupData, serverResponse, cmpId);
    }

    return {
      onboardingUrl: website,
      assetGroupData: {
        ...assetGroupData,
        FinalUrlForRecommendation: assetGroupData.FinalUrls[0],
      },
    };
  }).catch((err) => {
    send(WIZARD_EVENTS.UPDATE_WIZARD_DATA, { cache: undefined, isShowingAnimation: false });
    // clean cache and remove cacheId from scope if error occurs
    // because it probably means the assets from cache are not valid
    scope.remove(['cacheId', 'email', 'signature']);
    scope.history.replace({
      pathname: scope.history.location.pathname,
      search: scope.stringify(),
    });
    scope.save();
    uiActivity.error(`[CacheFlow][AdAssetsStep] Init from cache failed: ${JSON.stringify(err)}`);

    send(WIZARD_EVENTS.NAVIGATE_TO, {
      stepId: isCollectBusinessInfo
        ? PMAX_LITE_WIZARD_STEP_PATHS.COLLECT_BUSINESS_INFO
        : PMAX_LITE_WIZARD_STEP_PATHS.CAMPAIGN_CREATION_METHOD,
      flowFactors: { isCacheFlow: false },
    });
    return Promise.reject(err);
  }), [assetGroupId, cache, createAccountAndCampaign, createAssetGroupWhileLoading,
    createAssetGroupWithTempAssets, isCollectBusinessInfo, scope, send, uiActivity]);

  return readFromCache;
};
